import { isPlatformBrowser } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	output,
	PLATFORM_ID,
	signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownComponent } from 'ngx-markdown';
import type { Observable } from 'rxjs';
import {
	catchError,
	defer,
	distinctUntilChanged,
	EMPTY,
	forkJoin,
	map,
	of,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs';

import { CheckboxComponent } from '@valk-nx/components/ui-checkbox/src/lib/checkbox';
import { ModalComponent } from '@valk-nx/components/ui-modal/src/lib/modal';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { CookieBannerService } from '@valk-nx/services/cookie-manager/src/lib/cookie-banner.service';
import type { CookieKey } from '@valk-nx/services/cookie-manager/src/lib/cookie-manager.interface';
import { Status } from '@valk-nx/services/cookie-manager/src/lib/cookie-manager.interface';
import { GoogleTagManagerService } from '@valk-nx/services/google-tag-manager/src/lib/google-tag-manager.service';

import type { CookiePreferences } from './cookie-banner.interface';
import {
	COOKIE_DATA_LAYER_MAP,
	COOKIE_KEYS,
	CookieAction,
} from './cookie-banner.interface';
import { TrackingConsentGate } from './tracking-consent-gate';

@Component({
	selector: 'vp-cookie-banner',
	templateUrl: './cookie-banner.html',
	imports: [
		ModalComponent,
		TranslatePipe,
		TabMenuComponent,
		TabMenuItemDirective,
		CheckboxComponent,
		MarkdownComponent,
	],
})
export class CookieBannerComponent {
	readonly activeTab = signal<number>(0);

	private readonly cookieBannerService = inject(CookieBannerService);
	private readonly gtmService = inject(GoogleTagManagerService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);
	// Optional platform tracking gate (e.g. iOS App Tracking Transparency).
	// Only provided by native hosts; absent on web, where no gating applies.
	private readonly trackingConsentGate = inject(TrackingConsentGate, {
		optional: true,
	});

	language = input<string>('nl');

	private readonly language$ = toObservable(this.language);

	private readonly legalContent$ = this.language$.pipe(
		distinctUntilChanged(), // Prevent duplicate calls for same language
		switchMap((lang) => this.cookieBannerService.getLegal(lang)),
		shareReplay({ bufferSize: 1, refCount: true }), // Cache & cleanup
		catchError((error) => {
			console.error('Failed to load legal content:', error);
			return of(null);
		}),
	);

	// Convert back to signal for template usage
	readonly legalContent = toSignal(this.legalContent$, {
		initialValue: null,
	});

	private readonly urlFragment = toSignal(this.activatedRoute.fragment, {
		initialValue: null,
	});

	private readonly shouldShowFromUrl = computed(
		() => this.urlFragment() === 'showcookies',
	);

	readonly showModal = computed(
		() => this.shouldShowFromUrl() || this.manualShowModal(),
	);

	readonly manualShowModal = signal(false);

	readonly cookieValues = signal<CookiePreferences>({
		'valk-functional-cookie': true,
		'valk-analytics-cookie': false,
		'valk-marketing-cookie': false,
	});

	cookiesSelection = output<CookiePreferences>();

	private readonly cookiePreferences$ = defer(() => {
		// Load from API
		return forkJoin(
			COOKIE_KEYS.map((key) =>
				this.cookieBannerService.getCookie(key).pipe(
					map((status) => ({
						key,
						value: status === Status.ACCEPTED,
						isSet: status !== Status.EMPTY,
					})),
				),
			),
		).pipe(
			map((results) => {
				// Build preferences object with functional cookie always true
				const preferences = results.reduce(
					(acc, { key, value }) => ({
						...acc,
						[key]: key === 'valk-functional-cookie' ? true : value,
					}),
					{} as CookiePreferences,
				);

				const allSet = results.every((r) => r.isSet);

				return { preferences, allSet, results };
			}),
			// Apply the platform tracking gate silently: on init we must never
			// trigger a permission prompt, so the gate resolves from the known
			// authorization status only.
			switchMap(({ preferences, allSet, results }) =>
				this.applyTrackingConsent(preferences, false).pipe(
					map((gatedPreferences) => ({
						gatedPreferences,
						allSet,
						results,
					})),
				),
			),
			tap(({ gatedPreferences, allSet, results }) => {
				// Update signal with loaded (and gated) preferences
				this.cookieValues.set(gatedPreferences);
				this.manualShowModal.set(!allSet);
				this.pushInitCookieConsentToDataLayer(
					results,
					gatedPreferences,
				);
			}),
			shareReplay(1),
		);
	}).pipe(take(1));

	constructor() {
		// Auto-subscribe to load preferences (cleanup via take(1))
		this.cookiePreferences$.subscribe();
	}

	onCloseModal() {
		this.manualShowModal.set(false);
	}

	showAboutTab() {
		this.activeTab.set(2); // About is the 3rd tab (index 2)
	}

	setCookieValue(cookie: CookieKey, value: boolean): void {
		this.cookieValues.update((current) => ({
			...current,
			// Functional cookie cannot be disabled
			[cookie]: cookie === 'valk-functional-cookie' ? true : value,
		}));
	}

	handleCookies(action: CookieAction) {
		// Auto-subscribe with take(1) for cleanup
		this.saveCookiePreferences(action).subscribe();
	}

	private calculateCookieValues(action: CookieAction): CookiePreferences {
		switch (action) {
			case CookieAction.AcceptAll:
				return {
					'valk-functional-cookie': true,
					'valk-analytics-cookie': true,
					'valk-marketing-cookie': true,
				};
			case CookieAction.RejectAll:
				return {
					'valk-functional-cookie': true,
					'valk-analytics-cookie': false,
					'valk-marketing-cookie': false,
				};
			case CookieAction.Selection:
				return {
					...this.cookieValues(),
					'valk-functional-cookie': true, // Always enforce
				};
		}
	}

	private saveCookiePreferences(
		action: CookieAction,
	): Observable<CookiePreferences> {
		const requestedValues = this.calculateCookieValues(action);

		// Apply the platform tracking gate first. This is an explicit user
		// action, so the gate is allowed to prompt (e.g. show the iOS ATT
		// dialog). The persisted and pushed values reflect the gated result,
		// so tracking cookies are never enabled without authorization.
		return this.applyTrackingConsent(requestedValues, true).pipe(
			switchMap((newValues) => {
				const cookieKeys = Object.keys(newValues) as CookieKey[];

				return forkJoin(
					cookieKeys.map((key) =>
						this.cookieBannerService.setCookie(
							key,
							newValues[key] ? Status.ACCEPTED : Status.DECLINED,
						),
					),
				).pipe(map(() => newValues));
			}),
			tap((values) => {
				this.cookieValues.set(values);
				this.cookiesSelection.emit(values);
				this.manualShowModal.set(false);
				this.removeHash();
				this.pushCookieConsentToDataLayer(values);
			}),
			catchError((error) => {
				console.error('Failed to save cookie preferences:', error);
				return EMPTY;
			}),
			take(1),
		);
	}

	/**
	 * Reduce the requested analytics/marketing consent to what the platform
	 * tracking authorization allows. When no gate is provided (all web apps)
	 * the requested preferences are returned unchanged and synchronously.
	 */
	private applyTrackingConsent(
		preferences: CookiePreferences,
		prompt: boolean,
	): Observable<CookiePreferences> {
		if (!this.trackingConsentGate) {
			return of(preferences);
		}

		return this.trackingConsentGate
			.resolve(
				{
					analytics: preferences['valk-analytics-cookie'],
					marketing: preferences['valk-marketing-cookie'],
				},
				{ prompt },
			)
			.pipe(
				map((consent) => ({
					...preferences,
					'valk-analytics-cookie': consent.analytics,
					'valk-marketing-cookie': consent.marketing,
				})),
			);
	}

	removeHash() {
		if (this.isBrowser && globalThis.location.hash === '#showcookies') {
			this.router.navigate([], { fragment: undefined, replaceUrl: true });
		}
	}

	private pushCookieConsentToDataLayer(preferences: CookiePreferences): void {
		for (const key of COOKIE_KEYS) {
			const suffix = preferences[key] ? 'Accepted' : 'NotAccepted';
			this.gtmService.push({
				event: `${COOKIE_DATA_LAYER_MAP[key]}${suffix}`,
			});
		}
	}

	private pushInitCookieConsentToDataLayer(
		results: { key: CookieKey; value: boolean; isSet: boolean }[],
		preferences: CookiePreferences,
	): void {
		for (const { key, isSet } of results) {
			if (!isSet) {
				continue;
			}
			// Use the gated preference value so tracking consent is never
			// reported as accepted when the platform blocks tracking.
			const suffix = preferences[key] ? 'Accepted' : 'NotAccepted';
			this.gtmService.push({
				event: `${COOKIE_DATA_LAYER_MAP[key]}${suffix}`,
			});
		}
	}
}
